import React, { useState, useEffect, useCallback } from "react";
import { parse, stringify, stringifyUrl } from "query-string";

import BusinessDirectorySearch from "@/app/pages/businessDirectory/BusinessDirectorySearch";
import TabContactSearch from "@/app/pages/contactDirectory/TabContactSearch";

import {
	stringToTab,
	getPageNumberFragmentParameterName,
	getSearchFragmentParameterName,
	Tab
} from "@/app/pages/businessDirectory/helpers";
import { isUndefined } from "@/shared/FunctionCore";

const contactProfilesCompanyFragmentParameterName = "ContactProfilesCompany";
const contactProfilesFullNameFragmentParameterName = "ContactProfilesFullName";
const contactProfilesJobTitleFragmentParameterName = "ContactProfilesJobTitle";

interface FragmentAndUrlStateWithoutNavigationOnlyFragmentParameters {
	"fragmentParameters": Record<string, string>;
	"urlWithoutFragment": string;
}

type FragmentAndUrlState = FragmentAndUrlStateWithoutNavigationOnlyFragmentParameters & {
	"forceReload": boolean;
	"navigationOnlyFragmentParameters": Record<string, string>;
};

const parseWindowLocation: () => FragmentAndUrlStateWithoutNavigationOnlyFragmentParameters = () => {
	const windowLocation = window.location;
	const parsedQuery = parse(windowLocation.hash);
	const fragmentParametersFiltered: Record<string, string> = {};

	for (const [key, value] of window.Object.entries(parsedQuery)) {
		if (typeof value === "string") {
			fragmentParametersFiltered[key] = value;
		}
	}

	return {
		"fragmentParameters": fragmentParametersFiltered,
		"navigationOnlyFragmentParameters": {},
		"urlWithoutFragment": windowLocation.pathname,
	};
};

const tabKey = "tab";

const useFragmentAndUrlStateHook: () => [
	FragmentAndUrlState,
	(transformFragmentAndUrlState: (fragmentAndUrlState: FragmentAndUrlState) => FragmentAndUrlState) => void
] = () => {
	const [fragmentAndUrlState, setFragmentAndUrlState] = useState<FragmentAndUrlState>({
		...parseWindowLocation(),
		"forceReload": false,
		"navigationOnlyFragmentParameters": {},
	});

	useEffect(() => {
		const listener = () => {
			setFragmentAndUrlState((fragmentAndUrlStateTwo) => {
				return ({
					...parseWindowLocation(),
					"forceReload": fragmentAndUrlStateTwo.forceReload,
					"navigationOnlyFragmentParameters": fragmentAndUrlStateTwo.navigationOnlyFragmentParameters,
				})
			}
			);
		};

		window.addEventListener("hashchange", listener);

		return () => window.removeEventListener("hashchange", listener);
	}, []);

	const transformFragmentAndUrlState = useCallback(
		(transformFunction: (fragmentAndUrlState: FragmentAndUrlState) => FragmentAndUrlState) => {
			setFragmentAndUrlState((fragmentAndUrlStateTwo) => {
				const transformedFragmentAndUrlState = transformFunction(fragmentAndUrlStateTwo);

				// Update URL and fragment based on transformed state
				// Avoid using window.location.href or window.location.reload here

				const filteredFragmentAndNavigationOnlyFragmentParameters: Record<string, string> = {};
				const filteredFragmentParameters: Record<string, string> = {};

				for (const [key, value] of window.Object.entries(
					transformedFragmentAndUrlState.fragmentParameters
				).filter(([_, st]) => st.length > 0)) {
					filteredFragmentAndNavigationOnlyFragmentParameters[key] = value;
					filteredFragmentParameters[key] = value;
				}

				for (const [key, value] of window.Object.entries(
					transformedFragmentAndUrlState.navigationOnlyFragmentParameters
				).filter(([_, st]) => st.length > 0)) {
					filteredFragmentAndNavigationOnlyFragmentParameters[key] = value;
				}

				if (
					transformedFragmentAndUrlState.forceReload ||
					transformedFragmentAndUrlState.urlWithoutFragment !== fragmentAndUrlStateTwo.urlWithoutFragment
				) {
					const filteredFragmentAndNavigationOnlyFragmentParametersTab =
						filteredFragmentAndNavigationOnlyFragmentParameters[tabKey];

					const filteredFilteredFragmentAndNavigationOnlyFragmentParameters: Record<string, string> = {};

					if (filteredFragmentAndNavigationOnlyFragmentParametersTab) {
						const tab = stringToTab(filteredFragmentAndNavigationOnlyFragmentParametersTab)!;

						const allowedFragmentParameterNames = [tabKey, getPageNumberFragmentParameterName(tab)];

						if (tab === "Contact Directory") {
							allowedFragmentParameterNames.push(
								contactProfilesCompanyFragmentParameterName,
								contactProfilesFullNameFragmentParameterName,
								contactProfilesJobTitleFragmentParameterName
							);
						} else {
							allowedFragmentParameterNames.push(getSearchFragmentParameterName(tab));
						}

						for (const [key, value] of window.Object.entries(
							filteredFragmentAndNavigationOnlyFragmentParameters
						).filter(([st, _]) => allowedFragmentParameterNames.includes(st))) {
							filteredFilteredFragmentAndNavigationOnlyFragmentParameters[key] = value;
						}
					} else {
						for (const [key, value] of window.Object.entries(
							filteredFragmentAndNavigationOnlyFragmentParameters
						)) {
							filteredFilteredFragmentAndNavigationOnlyFragmentParameters[key] = value;
						}
					}

					window.location.href = stringifyUrl(
						{
							"fragmentIdentifier": stringify(
								filteredFilteredFragmentAndNavigationOnlyFragmentParameters
							),
							"url": transformedFragmentAndUrlState.urlWithoutFragment,
						},
						{ "encode": false }
					);

					if (transformedFragmentAndUrlState.forceReload) {
						window.location.reload();
					}
				} else {
					window.location.hash = stringify(filteredFragmentParameters);
				}

				return transformedFragmentAndUrlState;
			});
		},
		[]
	);

	return [fragmentAndUrlState, transformFragmentAndUrlState];
};

const TabsAndInputSearchBox = (props) => {
	const {
		isSearchOverlay,
		countryCode,
		languageCode,
		isBusinessDirectoryLandingPage,
		singlePageSearchData: {
			currentPagePath,
			siteSearchResultsPath,
			errorHandlerPath,
			siteSearchResultsPathRewritten,
			isEuBd
		} } = props
	// const [activeTab, setActiveTab] = useState<string>("Business Directory");

	const isSiteSearchResultsPage = currentPagePath.endsWith(siteSearchResultsPath);
	const isContactDirectoryLandingPage = currentPagePath.endsWith("/contact-directory.html");
	const isErrorPage = currentPagePath.includes(errorHandlerPath);

	const isBusinessDirectoryLandingPageOrContactDirectoryLandingPageOrErrorPage =
		isBusinessDirectoryLandingPage || isContactDirectoryLandingPage || isErrorPage;

	const [fragmentAndUrlState, setFragmentAndUrlState] = useFragmentAndUrlStateHook();

	const useFragmentAndUrlStateTriggerNavigation: (
		key: string,
		defaultValue: string,
		outerTriggerNavigation: boolean
	) => [string, (newValue: string) => void] = (
		key: string,
		defaultValue: string,
		outerTriggerNavigation: boolean
	) => {
			const setValue = (newValue: string, triggerNavigation: boolean) =>
				setFragmentAndUrlState((fragmentAndUrlStateTwo) => {
					return ({
						"forceReload": isSearchOverlay && isSiteSearchResultsPage && triggerNavigation && key !== tabKey,
						"fragmentParameters": isSearchOverlay
							? fragmentAndUrlStateTwo.fragmentParameters
							: {
								...fragmentAndUrlStateTwo.fragmentParameters,
								[key]: newValue,
							},
						"navigationOnlyFragmentParameters": isSearchOverlay
							? {
								...fragmentAndUrlStateTwo.navigationOnlyFragmentParameters,
								[key]: newValue,
							}
							: fragmentAndUrlStateTwo.navigationOnlyFragmentParameters,
						
							// this FALSE STATEMENT HAVE TO BE REMOVED taking care of BD Landing page redirection

						"urlWithoutFragment": false &&
							triggerNavigation &&
								key !== tabKey &&
								(isBusinessDirectoryLandingPageOrContactDirectoryLandingPageOrErrorPage || isSearchOverlay)
								? siteSearchResultsPathRewritten
								: fragmentAndUrlStateTwo.urlWithoutFragment,
					})
				})

			let valueToUse;

			if (isSearchOverlay) {
				const valueFromNavigationOnlyFragmentParameters = fragmentAndUrlState.navigationOnlyFragmentParameters[key];

				valueToUse = valueFromNavigationOnlyFragmentParameters
					? valueFromNavigationOnlyFragmentParameters
					: defaultValue;
			} else {
				const valueFromFragmentParameters = fragmentAndUrlState.fragmentParameters[key];

				if (isUndefined(valueFromFragmentParameters)) {
					setValue(defaultValue, false);

					valueToUse = defaultValue;
				} else {
					valueToUse = valueFromFragmentParameters;
				}
			}

			return [valueToUse, (newValue: string) => setValue(newValue, outerTriggerNavigation)];
		};

	let defaultActiveTab: Tab;
	// const isUnitedStates = siteNode === "dnb-mod" || siteNode === "dnb-us";
	const isUnitedStates = false;
	const isSearchOverlayAndNotUnitedStates = !isUnitedStates && isSearchOverlay;

	if (isSearchOverlayAndNotUnitedStates && !isEuBd) {
		defaultActiveTab = "Site Content";
	} else if (isBusinessDirectoryLandingPage) {
		defaultActiveTab = "Business Directory";
	} else if (isContactDirectoryLandingPage) {
		defaultActiveTab = "Contact Directory";
	} else {
		defaultActiveTab = "Business Directory";
	}

	const useFragmentAndUrlState: (key: string, defaultValue: string) => [string, (newValue: string) => void] = (
		key: string,
		defaultValue: string
	) => useFragmentAndUrlStateTriggerNavigation(key, defaultValue, true);

	const manuallyTriggerNavigation = () =>
		setFragmentAndUrlState((fragmentAndUrlStateTwo) => ({
			"forceReload": isSearchOverlay && isSiteSearchResultsPage,
			"fragmentParameters": fragmentAndUrlStateTwo.fragmentParameters,
			"navigationOnlyFragmentParameters": fragmentAndUrlStateTwo.navigationOnlyFragmentParameters,
			"urlWithoutFragment":
				isBusinessDirectoryLandingPageOrContactDirectoryLandingPageOrErrorPage || isSearchOverlay
						? siteSearchResultsPathRewritten
						: fragmentAndUrlStateTwo.urlWithoutFragment,
	}));

	const [tabFromFragmentAndUrlState, setTabFromFragmentAndUrlState] = useFragmentAndUrlState(
		tabKey,
		defaultActiveTab
	);

	const [activeTab, setActiveTab] = [
		stringToTab(tabFromFragmentAndUrlState)!,
		(tab: Tab) => setTabFromFragmentAndUrlState(tab),
	];

	const [companyProfilesSearchValue, setCompanyProfilesSearchValue] = useFragmentAndUrlState(
		getSearchFragmentParameterName(activeTab),
		""
	);

	const inputSearchProps = {
		...props, activeTab, useFragmentAndUrlState, setCompanyProfilesSearchValue, companyProfilesSearchValue,
		getPageNumberFragmentParameterName, isBusinessDirectoryLandingPageOrContactDirectoryLandingPageOrErrorPage,
		isBusinessDirectoryLandingPage, countryCode, languageCode, isSiteSearchResultsPage, useFragmentAndUrlStateTriggerNavigation,
		manuallyTriggerNavigation
	}

	return (
		<>
			{/* <Tabs
				activeTab={activeTab}
				setActiveTab={setActiveTab}
			/> */}
			{(isBusinessDirectoryLandingPage || (isSiteSearchResultsPage && activeTab === "Business Directory")) &&
				<BusinessDirectorySearch {...inputSearchProps} />
			}	
			{(isContactDirectoryLandingPage || (isSiteSearchResultsPage && activeTab === "Contact Directory")) && 
				<TabContactSearch {...inputSearchProps} />
			}
		</>
	);
}

export default TabsAndInputSearchBox;
